/**
 * "Use existing" — pick a video (or image) already in the advertiser's TikTok
 * creative library instead of uploading again. Two sources:
 *   • Library:  /file/video/ad/search/ (or /file/image/ad/search/) — every
 *               upload on the advertiser, ready or still transcoding.
 *   • Past ads: distinct creatives from the CRM's synced ads, with the copy
 *               they ran with, so a whole past creative can be reused.
 */
import React, { useEffect, useMemo, useState } from 'react';
import clsx from 'clsx';
import { CheckCircle2, Clock, Film, Image as ImageIcon, RefreshCw, Search } from 'lucide-react';
import { Modal } from '@/components/Modal';
import { Button } from '@/components/Button';
import { tiktokService, type TiktokAdCreativeRef, type TiktokLibraryVideo } from '@/services/tiktok.service';
import { errMsg, fmtDuration } from './workspaceShared';

type LibraryImage = { imageId: string; imageUrl: string; imagekitUrl?: string; width?: number; height?: number; fileName?: string };

export type PickedVideo = { videoId: string; coverUrl?: string; previewUrl?: string; fileName?: string; displayable?: boolean };
export type PickedImage = { imageId: string; url?: string; fileName?: string };
export type PickedFromAd = TiktokAdCreativeRef;

export function ExistingCreativePicker({
  open, onClose, kind, onPickVideo, onPickImage, onPickFromAd,
}: {
  open: boolean;
  onClose: () => void;
  kind: 'video' | 'image';
  onPickVideo: (v: PickedVideo) => void;
  onPickImage: (i: PickedImage) => void;
  /** Reuse a past ad's creative + copy. */
  onPickFromAd: (ref: PickedFromAd) => void;
}) {
  const [tab, setTab] = useState<'library' | 'ads'>('library');
  const [videos, setVideos] = useState<TiktokLibraryVideo[] | null>(null);
  const [images, setImages] = useState<LibraryImage[] | null>(null);
  const [fromAds, setFromAds] = useState<TiktokAdCreativeRef[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState('');

  const load = async (force = false) => {
    setLoading(true); setError(null);
    try {
      if (tab === 'library') {
        if (kind === 'video') {
          if (force || videos === null) setVideos(await tiktokService.listVideos({ pageSize: 100 }));
        } else if (force || images === null) {
          setImages(await tiktokService.listImages({ pageSize: 100 }));
        }
      } else if (force || fromAds === null) {
        setFromAds(await tiktokService.listCreativesFromAds({ limit: 100 }));
      }
    } catch (e) {
      // The axios interceptor already toasts API errors; keep an inline message for context.
      setError(errMsg(e, 'Could not load creatives from TikTok.'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!open) return;
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, tab, kind]);

  const q = query.trim().toLowerCase();
  const filteredVideos = useMemo(
    () => (videos ?? []).filter((v) => !q || (v.fileName ?? '').toLowerCase().includes(q) || v.videoId.includes(q)),
    [videos, q],
  );
  const filteredImages = useMemo(
    () => (images ?? []).filter((i) => !q || (i.fileName ?? '').toLowerCase().includes(q) || i.imageId.includes(q)),
    [images, q],
  );
  const filteredAds = useMemo(
    () => (fromAds ?? []).filter((a) => {
      const matchesKind = kind === 'video' ? !!a.videoId : (a.imageIds?.length ?? 0) > 0 && !a.videoId;
      return matchesKind && (!q || a.adName.toLowerCase().includes(q) || (a.adText ?? '').toLowerCase().includes(q));
    }),
    [fromAds, q, kind],
  );

  const TabBtn = ({ k, label }: { k: 'library' | 'ads'; label: string }) => (
    <button
      type="button"
      onClick={() => setTab(k)}
      className={clsx(
        'rounded-md px-3 py-1.5 text-xs font-medium transition-all',
        tab === k ? 'bg-surface text-text-primary shadow-soft' : 'text-text-muted hover:text-text-primary',
      )}
    >
      {label}
    </button>
  );

  return (
    <Modal open={open} onClose={onClose} title={kind === 'video' ? 'Use an existing video' : 'Use an existing image'} widthClass="max-w-3xl">
      <div className="space-y-3">
        <div className="flex flex-wrap items-center gap-2">
          <div className="inline-flex gap-0.5 rounded-lg border border-border bg-surface-alt p-0.5">
            <TabBtn k="library" label="Creative library" />
            <TabBtn k="ads" label="From past ads" />
          </div>
          <div className="relative min-w-[200px] flex-1">
            <Search size={13} className="pointer-events-none absolute inset-y-0 left-3 my-auto text-text-muted" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={tab === 'library' ? 'Filter by file name or id…' : 'Filter by ad name or text…'}
              className="w-full rounded-lg border border-border bg-surface py-1.5 pl-8 pr-3 text-xs text-text-primary placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-primary/40"
            />
          </div>
          <Button variant="secondary" size="sm" onClick={() => load(true)} loading={loading} iconLeft={<RefreshCw size={12} />}>
            Refresh
          </Button>
        </div>

        {error && (
          <div className="rounded-xl border border-danger/30 bg-danger-soft p-3 text-xs text-danger">{error}</div>
        )}

        {loading && (videos === null && images === null && fromAds === null) && (
          <div className="py-10 text-center text-xs text-text-muted">Loading from TikTok…</div>
        )}

        {tab === 'library' && kind === 'video' && videos !== null && (
          filteredVideos.length === 0 ? (
            <EmptyNote text={videos.length === 0 ? 'No videos in this advertiser\'s library yet — upload one on the ad instead.' : 'No videos match.'} />
          ) : (
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4">
              {filteredVideos.map((v) => (
                <button
                  key={v.videoId}
                  type="button"
                  onClick={() => {
                    onPickVideo({ videoId: v.videoId, coverUrl: v.videoCoverUrl, previewUrl: v.previewUrl, fileName: v.fileName, displayable: v.displayable });
                    onClose();
                  }}
                  className="group overflow-hidden rounded-xl border border-border bg-surface text-left transition-colors hover:border-primary"
                >
                  <div className="relative aspect-[9/16] max-h-44 w-full bg-neutral-900">
                    {v.videoCoverUrl
                      ? <img src={v.videoCoverUrl} alt="" className="h-full w-full object-cover" loading="lazy" referrerPolicy="no-referrer" />
                      : <div className="flex h-full items-center justify-center text-neutral-500"><Film size={22} /></div>}
                    <span className={clsx(
                      'absolute left-1.5 top-1.5 inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[10px] font-semibold',
                      v.displayable === false ? 'bg-warning text-white' : 'bg-success text-white',
                    )}>
                      {v.displayable === false ? <><Clock size={9} /> transcoding</> : <><CheckCircle2 size={9} /> ready</>}
                    </span>
                    {fmtDuration(v.duration) && (
                      <span className="absolute bottom-1.5 right-1.5 rounded bg-black/70 px-1 py-px font-mono text-[10px] text-white">{fmtDuration(v.duration)}</span>
                    )}
                  </div>
                  <div className="p-2">
                    <div className="truncate text-xs font-medium text-text-primary" title={v.fileName}>{v.fileName || v.videoId}</div>
                    <div className="mt-0.5 truncate text-[10px] text-text-muted">
                      {v.width && v.height ? `${v.width}×${v.height}` : 'size unknown'}
                      {v.createTime ? ` · ${new Date(v.createTime).toLocaleDateString()}` : ''}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )
        )}

        {tab === 'library' && kind === 'image' && images !== null && (
          filteredImages.length === 0 ? (
            <EmptyNote text={images.length === 0 ? 'No images in this advertiser\'s library yet.' : 'No images match.'} />
          ) : (
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4">
              {filteredImages.map((im) => (
                <button
                  key={im.imageId}
                  type="button"
                  onClick={() => { onPickImage({ imageId: im.imageId, url: im.imagekitUrl ?? im.imageUrl, fileName: im.fileName }); onClose(); }}
                  className="overflow-hidden rounded-xl border border-border bg-surface text-left transition-colors hover:border-primary"
                >
                  <div className="aspect-square w-full bg-neutral-900">
                    <img src={im.imagekitUrl ?? im.imageUrl} alt="" className="h-full w-full object-cover" loading="lazy" referrerPolicy="no-referrer" />
                  </div>
                  <div className="p-2">
                    <div className="truncate text-xs font-medium text-text-primary">{im.fileName || im.imageId}</div>
                    <div className="mt-0.5 text-[10px] text-text-muted">{im.width && im.height ? `${im.width}×${im.height}` : ''}</div>
                  </div>
                </button>
              ))}
            </div>
          )
        )}

        {tab === 'ads' && fromAds !== null && (
          filteredAds.length === 0 ? (
            <EmptyNote text="No past ads with a matching creative. Sync ads under TikTok → Campaigns to populate this list." />
          ) : (
            <ul className="space-y-1.5">
              {filteredAds.map((a) => (
                <li key={a.tiktokAdId}>
                  <button
                    type="button"
                    onClick={() => { onPickFromAd(a); onClose(); }}
                    className="flex w-full items-center gap-3 rounded-xl border border-border bg-surface px-3 py-2 text-left transition-colors hover:border-primary"
                  >
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-surface-alt text-text-muted">
                      {a.videoId ? <Film size={15} /> : <ImageIcon size={15} />}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-xs font-semibold text-text-primary">{a.adName}</span>
                      <span className="block truncate text-[11px] text-text-muted">{a.adText || '—'}</span>
                    </span>
                    <span className="shrink-0 font-mono text-[10px] text-text-muted">{a.videoId ?? a.imageIds?.[0]}</span>
                  </button>
                </li>
              ))}
            </ul>
          )
        )}
      </div>
    </Modal>
  );
}

const EmptyNote = ({ text }: { text: string }) => (
  <div className="rounded-xl border border-dashed border-border bg-surface-alt/40 px-4 py-8 text-center text-xs text-text-muted">{text}</div>
);
